import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, SecurityContext, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { DomSanitizer } from '@angular/platform-browser';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { ProfileDetails } from '../../../components/interfaces/myStories.interface';
import { EventInfo } from '../../../interfaces/event-info.interface';

@Injectable({
    providedIn: 'root',
})
export class MyStoriesService {
    private _participantInfo: EventInfo = <any>null;
    private userProfileDetails: ProfileDetails = <any>null;
    private _ciamId: string = '';
    eventIdOfEvent!: string | null;
    vinOrFinOfCar!: string | null;
    private userCountryRegion = localStorage.getItem('countryOfOrigin') ?? '';

    private readonly API_ENDPOINT = environment.proxyEndpoint;

    constructor(
        private readonly _httpClient: HttpClient,
        private readonly sanitizer: DomSanitizer,
    ) {
        if (this.userCountryRegion !== '') {
            const userCountryData = JSON.parse(this.userCountryRegion);
            const region = userCountryData.region;

            if (region === 'amap') {
                this.API_ENDPOINT = environment.proxyAmapEndpoint;
            }
        }
    }

    public set particpantInfoResponse(data: EventInfo) {
        this._participantInfo = data;
    }

    public get particpantInfoResponse(): EventInfo {
        return this._participantInfo;
    }

    private readonly editProfileClickedState = signal<boolean>(false);
    private readonly editProfileClosedState = signal<boolean>(false);

    private readonly _isProfileUpdated = signal<boolean>(false);
    private readonly isProfileUpdated$ = toObservable(this._isProfileUpdated);

    editProfileClicked$ = toObservable(this.editProfileClickedState);
    editProfileClosed$ = toObservable(this.editProfileClosedState);

    private readonly showSpinnerState = signal<boolean>(false);
    readonly showSpinnerSubject$ = toObservable(this.showSpinnerState);

    private readonly showEmptyEventGarageState = signal<boolean>(false);
    readonly showEmptyEventGarageSubject$ = toObservable(this.showEmptyEventGarageState);

    private readonly emptyCarGarageState = signal<boolean>(false);
    readonly emptyCarGarageSubject$ = toObservable(this.emptyCarGarageState);

    private readonly showUrlIncludesEventsState = signal<boolean>(false);
    readonly showUrlIncludesEventsSubject$ = toObservable(this.showUrlIncludesEventsState);

    showUrlIncludesEvents(): void {
        this.showUrlIncludesEventsState.set(true); // Emit true when called
    }

    hideUrlIncludesEvents(): void {
        this.showUrlIncludesEventsState.set(false); // Emit false when called
    }

    public showEventGarage(): void {
        this.showEmptyEventGarageState.set(true);
    }

    public hideEventGarage(): void {
        this.showEmptyEventGarageState.set(false);
    }

    public setCarGarageEmpty(isEmpty: boolean): void {
        this.emptyCarGarageState.set(isEmpty);
    }

    public showSpinner(): void {
        this.showSpinnerState.set(true);
    }

    public hideSpinner(): void {
        this.showSpinnerState.set(false);
    }

    public editProfilePopupVisible(visible: boolean): void {
        this.editProfileClickedState.set(visible);
    }

    public editProfilePopupClosed(): void {
        this.editProfileClosedState.set(true);
    }

    public setIsProfileUpdate(isUpdated: boolean): void {
        this._isProfileUpdated.set(isUpdated);
    }

    public getIsProfileUpdated(): Observable<boolean> {
        return this.isProfileUpdated$;
    }

    public set profileInfoResponse(data: ProfileDetails) {
        this.userProfileDetails = data;
    }

    public get profileInfoResponse(): ProfileDetails {
        return this.userProfileDetails;
    }

    public set ciamId(userId: string) {
        this._ciamId = userId;
    }

    public get ciamId(): string {
        return this._ciamId;
    }

    // getter and setter for setting vin and eventId
    set eventId(id: string | null) {
        this.eventIdOfEvent = id;
    }

    get eventId(): string | null {
        return this.eventIdOfEvent;
    }

    set vinOrFin(id: string | null) {
        this.vinOrFinOfCar = id;
    }
    get vinOrFin(): string | null {
        return this.vinOrFinOfCar;
    }

    /**
     * @description Fetches garage details for a given user
     * @param ciamId
     * @returns Observable
     */
    public getGarageDetails(ciamId?: any): Observable<any> {
        const headers = new HttpHeaders()
            .set('ciamId', ciamId)
            .set('Content-Type', 'application/json');
        let url = '';
        if (localStorage.getItem('isLoggedIn') === 'true') {
            url = this.API_ENDPOINT + '/stories/getGarageData';
        } else {
            url = this.API_ENDPOINT + '/stories/getPublicGarageData';
        }
        const body = {
            latestTimestamp: 0,
        };
        return this._httpClient.post(url, body, { headers: headers, withCredentials: true });
    }

    /**
     * @description Fetches stories for a given VIN or FIN
     * @param vinOrFin
     * @param ciamId
     * @param pageNumber
     * @param pageSize
     * @returns Obserbable
     */
    public getStroiesForVin(
        vinOrFin: any,
        ciamId: any,
        pageNumber: number,
        pageSize: number,
    ): Observable<any> {
        const api_header = {
            headers: { 'Content-Type': 'application/json', vinOrFin: vinOrFin, ciamId: ciamId },
        };
        let url = '';
        if (localStorage.getItem('isLoggedIn') === 'true') {
            url = `${this.API_ENDPOINT}/stories/getStories?pageSize=${pageSize}&pageNumber=${pageNumber}`;
        } else {
            url = `${this.API_ENDPOINT}/stories/getPublicStoriesForCar?pageSize=${pageSize}&pageNumber=${pageNumber}`;
        }
        return this._httpClient.get<any>(url, {
            headers: api_header.headers,
            withCredentials: true,
        });
    }

    /**
     * @description Fetches user details and displays in header, garage and edit profile
     * @param userId
     * @returns Observable
     */
    public getProfile(userId?: any): Observable<any> {
        let api_header = { headers: {} };
        if (userId) {
            api_header = {
                headers: { userId: userId },
            };
        }
        let url = '';
        if (localStorage.getItem('isLoggedIn') === 'true') {
            url = `${this.API_ENDPOINT}/stories/getUser?isStories=1`;
        } else {
            url = `${this.API_ENDPOINT}/stories/getPublicProfileData`;
        }
        return this._httpClient.get<any>(url, {
            headers: api_header.headers,
            withCredentials: true,
        });
    }

    /**
     * @description This API is called in /register/:eventId route
     * Fetches user details and displays in slef-regsitration flow
     * @param eventId
     * @returns Observable
     */
    public getUserInfoForSelfRegistration(eventId: string): Observable<any> {
        const baseUrl = this.API_ENDPOINT;
        const url = `${baseUrl}/stories/events/fetchRegistrationDetails/${eventId}`;
        return this._httpClient.get(url, { withCredentials: true });
    }

    /**
     * @description This API is called on click of register button in Self-registration flow
     * @param: Event ID
     */
    public registerUser(eventId: string): Observable<any> {
        const baseUrl = this.API_ENDPOINT;
        const url = `${baseUrl}/stories/events/registerUser/${eventId}`;
        return this._httpClient.post(url, {}, { withCredentials: true });
    }

    /**
     * @description This API is called on click of Update button in edit profile popup.
     * Can update nickname, description and social link
     * @param nickName
     * @param description
     * @param socialLink
     * @param userId
     * @returns Observablef
     */
    public updateProfile(
        nickName: string,
        description: string,
        socialLink: string,
        userId: any,
    ): Observable<any> {
        const api_header = { 'Content-Type': 'application/json', userId: userId };
        const baseUrl = this.API_ENDPOINT + '/stories/updateUser?isStories=1';
        const url = `${baseUrl}`;
        return this._httpClient.put(
            url,
            { nickName, description, socialLink },
            { headers: api_header, withCredentials: true },
        );
    }

    /**
     * @description called post successful CIAM login; will have customer details and
     * event details. Called when we reach the link-participant page
     * @param participantId
     * @param participantKey
     * @returns Participant details and event details
     */
    public getParticipantInfo(participantId: any, participantKey: any): Observable<any> {
        const baseUrl = this.API_ENDPOINT;
        const url = `${baseUrl}/stories/participants/getParticipantById/${participantKey}/${participantId}`;
        return this._httpClient.get(url, { withCredentials: true });
    }

    /**
     * @description called on confirm click and refresh
     * @param participantId
     * @param participantKey
     * @returns event data
     */
    public updateCiamConfirmation(participantId: any, participantKey: any): Observable<any> {
        const baseUrl = this.API_ENDPOINT;
        const url = `${baseUrl}/stories/participants/confirmParticipantLink/${participantKey}/${participantId}`;
        return this._httpClient.put(url, {}, { withCredentials: true });
    }

    public isValidGuID(guID: any) {
        const regex = /^[{]?[0-9a-fA-F]{8}-([0-9a-fA-F]{4}-){3}[0-9a-fA-F]{12}[}]?$/;
        return regex.test(guID);
    }
    public getEventDetails(eventId: any): Observable<any> {
        let api_header = {};
        if (eventId) {
            api_header = {
                headers: { eventid: eventId },
            };
        }

        const baseUrl = this.API_ENDPOINT;
        const url = `${baseUrl}/stories/events/getEventData`;
        return this._httpClient.get(url, api_header);
    }

    public getCIAMandEventInfo(eventId: any): Observable<any> {
        const baseUrl = this.API_ENDPOINT;
        const url = `${baseUrl}/stories/events/getCIAMandEventInfo/${eventId}`;
        return this._httpClient.get(url, { withCredentials: true });
    }

    public getEventInfoForVerify(participantId: any, participantSecretKey: any): Observable<any> {
        participantId = this.sanitizer.sanitize(SecurityContext.URL, participantId);
        participantSecretKey = this.sanitizer.sanitize(SecurityContext.URL, participantSecretKey);
        const baseUrl = this.API_ENDPOINT;
        const url = `${baseUrl}/stories/events/getEventInfo/${participantSecretKey}/${participantId}`;
        return this._httpClient.get(url, { withCredentials: true });
    }

    public getEventDetailsInfoForVerify(
        participantId: any,
        participantSecretKey: any,
    ): Observable<any> {
        participantId = this.sanitizer.sanitize(SecurityContext.URL, participantId);
        participantSecretKey = this.sanitizer.sanitize(SecurityContext.URL, participantSecretKey);
        const baseUrl = this.API_ENDPOINT;
        const url = `${baseUrl}/stories/events/getEventCIAMLinkDetailsInfo/${participantSecretKey}/${participantId}`;
        return this._httpClient.get(url, { withCredentials: true });
    }

    public verifyParticipant(participantId: any, participantSecretKey: any): Observable<any> {
        participantId = this.sanitizer.sanitize(SecurityContext.URL, participantId);
        participantSecretKey = this.sanitizer.sanitize(SecurityContext.URL, participantSecretKey);
        const baseUrl = this.API_ENDPOINT;
        const url = `${baseUrl}/stories/events/verifyParticipant/${participantId}/${participantSecretKey}`;
        return this._httpClient.put(url, {}, { withCredentials: true });
    }

    public registerForEvent(eventId: any): Observable<any> {
        const baseUrl = this.API_ENDPOINT;
        const url = `${baseUrl}/stories/events/selfRegistration/${eventId}`;
        return this._httpClient.post(url, {}, { withCredentials: true });
    }

    private updateShowSpinnerState(isVisible: boolean): void {
        this.showSpinnerState.set(isVisible);
    }

    private updateShowEmptyEventGarageState(isVisible: boolean): void {
        this.showEmptyEventGarageState.set(isVisible);
    }

    private updateShowUrlIncludesEventsState(isVisible: boolean): void {
        this.showUrlIncludesEventsState.set(isVisible);
    }

    public getParticipantQrForSelfReg(eventId: any, participantId: any): Observable<any> {
        const baseUrl = this.API_ENDPOINT;
        const url = `${baseUrl}/stories/events/getParticipantQR/${eventId}/${participantId}`;

        return this._httpClient.get(url, { withCredentials: true, responseType: 'blob' });
    }

    public getAllEventIds(ciamId: any): Observable<any> {
        const headers = new HttpHeaders()
            .set('ciamId', ciamId)
            .set('Content-Type', 'application/json');
        let url = '';
        if (localStorage.getItem('isLoggedIn') === 'true') {
            url = this.API_ENDPOINT + '/stories/events/getAllEventsData';
        } else {
            url = this.API_ENDPOINT + '/stories/events/getPublicEventsData';
        }
        const body = {
            latestTimestamp: 0,
        };
        return this._httpClient.post(url, body, { headers: headers, withCredentials: true });
    }

    public getAllEventsData(eventIds: any, ciamId: any): Observable<any> {
        const headers = new HttpHeaders()
            .set('ciamId', ciamId)
            .set('Content-Type', 'application/json');
        let url = '';
        if (localStorage.getItem('isLoggedIn') === 'true') {
            url = this.API_ENDPOINT + '/stories/events/getEventInfoForEventIds';
        } else {
            url = this.API_ENDPOINT + '/stories/events/getPublicEventInfoForEventIds';
        }
        const body = {
            eventIds: eventIds,
        };
        return this._httpClient.post(url, body, { headers: headers, withCredentials: true });
    }

    public getAllStoriesForEventIds(
        eventId: any,
        pageNumber: number,
        pageSize: number,
        ciamId: any,
    ): Observable<any> {
        const headers = new HttpHeaders()
            .set('ciamId', ciamId)
            .set('Content-Type', 'application/json');
        let url = '';
        if (localStorage.getItem('isLoggedIn') === 'true') {
            url = `${this.API_ENDPOINT}/stories/events/getAllStoriesForEventIds?eventId=${eventId}&page_size=${pageSize}&page_number=${pageNumber}`;
        } else {
            url = `${this.API_ENDPOINT}/stories/events/getAllPublicStoriesForEventIds?eventId=${eventId}&page_size=${pageSize}&page_number=${pageNumber}`;
        }
        return this._httpClient.get<any>(url, { headers: headers, withCredentials: true });
    }
}
